/**
 * MyDriversPanel.jsx
 *
 * Carrier "My Drivers" tab — shows all finalized (transferred) drivers.
 * For each driver, the carrier (and safety team) can:
 *   - View the full unlocked profile (phone, email, employment history)
 *   - Open a direct chat room specific to this transfer
 *   - See payment status
 */

import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone, Mail, Briefcase, Truck, ChevronRight, MessageSquare,
  Loader2, Unlock, Shield, X, User, FileText, ChevronDown, ChevronUp,
} from "lucide-react";
import { api } from "../../lib/api";
import { ChatRoom } from "./ChatRoom";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const CARD   = "#161618";
const GREEN  = "#22C55E";

function FullProfileModal({ appId, driverName, bidId, token, hub, onClose }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [expandEmp, setExpandEmp] = useState(false);

  useEffect(() => {
    api.marketGetFullDriverProfile(token, appId)
      .then((d) => setProfile(d))
      .catch(() => toast.error("Failed to load profile"))
      .finally(() => setLoading(false));
  }, [appId]);

  const chatRoom = `transfer_${bidId}`;

  return (
    <motion.div className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.88)" }}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        style={{ background: "#0A0A0A", border: `1px solid ${GREEN}30` }}
        initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.97, opacity: 0 }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", background: "rgba(34,197,94,0.04)" }}>
          <div className="flex items-center gap-3">
            <Unlock className="w-4 h-4" style={{ color: GREEN }} />
            <div>
              <h3 className="font-head text-base font-black text-white">{driverName}</h3>
              <p className="font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: GREEN }}>Full Profile Unlocked</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowChat((v) => !v)} data-testid={`driver-chat-btn-${appId}`}
              className="flex items-center gap-1.5 px-3 py-1.5 font-head text-[9px] uppercase tracking-[0.15em] font-black transition-all"
              style={{ background: showChat ? "rgba(255,77,0,0.15)" : "rgba(255,255,255,0.05)", color: showChat ? ACCENT : "rgba(255,255,255,0.5)", border: `1px solid ${showChat ? ACCENT + "40" : "rgba(255,255,255,0.1)"}` }}>
              <MessageSquare className="w-3 h-3" />
              {showChat ? "Hide Chat" : "Chat"}
            </button>
            <button onClick={onClose} className="p-1.5 transition-colors hover:text-white" style={{ color: "rgba(255,255,255,0.3)" }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} /></div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Contact info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 px-4 py-3" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
                <Phone className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />
                <div>
                  <p className="font-head text-[8px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.3)" }}>Phone</p>
                  <a href={`tel:${profile?.phone}`} className="text-sm font-bold text-white hover:underline">{profile?.phone || "—"}</a>
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-3" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.07)" }}>
                <Mail className="w-4 h-4 shrink-0" style={{ color: GOLD }} />
                <div>
                  <p className="font-head text-[8px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.3)" }}>Email</p>
                  <a href={`mailto:${profile?.email}`} className="text-sm font-bold text-white hover:underline truncate block">{profile?.email || "—"}</a>
                </div>
              </div>
            </div>

            {/* Driver details */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { label: "CDL Class", value: profile?.cdl_class },
                { label: "Position",  value: profile?.position },
                { label: "State",     value: profile?.state },
                { label: "Experience", value: profile?.cdl_experience },
              ].map(({ label, value }) => (
                <div key={label} className="px-3 py-2.5" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)" }}>
                  <p className="font-head text-[7px] uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.25)" }}>{label}</p>
                  <p className="text-sm font-bold text-white mt-0.5">{value || "—"}</p>
                </div>
              ))}
            </div>

            {/* Documents status */}
            <div>
              <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>Documents on File</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "CDL",           has: profile?.has_cdl },
                  { label: "Medical Cert",  has: profile?.has_medical },
                  { label: "MVR",           has: profile?.has_mvr },
                  { label: "PSP",           has: profile?.has_psp },
                  { label: "Clearinghouse", has: profile?.has_clearinghouse },
                ].map(({ label, has }) => (
                  <span key={label} className="flex items-center gap-1.5 px-2.5 py-1 font-head text-[9px] uppercase tracking-[0.1em]"
                    style={{ background: has ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.03)", color: has ? GREEN : "rgba(255,255,255,0.2)", border: `1px solid ${has ? "rgba(34,197,94,0.25)" : "rgba(255,255,255,0.07)"}` }}>
                    <FileText className="w-2.5 h-2.5" /> {label}
                  </span>
                ))}
              </div>
            </div>

            {/* Employment history */}
            {(profile?.employment_history || []).length > 0 && (
              <div>
                <button onClick={() => setExpandEmp((v) => !v)}
                  className="flex items-center gap-2 font-head text-[9px] uppercase tracking-[0.3em] mb-2 hover:opacity-80 transition-opacity"
                  style={{ color: "rgba(255,255,255,0.3)" }}>
                  <Briefcase className="w-3 h-3" />
                  Employment History ({profile.employment_history.length})
                  {expandEmp ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
                <AnimatePresence>
                  {expandEmp && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden space-y-1.5">
                      {profile.employment_history.map((job, i) => (
                        <div key={i} className="px-4 py-3" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)" }}>
                          <p className="text-sm font-bold text-white">{job.company_name || job.employer || "Company"}</p>
                          <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                            {[job.position, job.dates, job.city && `${job.city}, ${job.state || ""}`].filter(Boolean).join(" · ")}
                          </p>
                          {job.reason_for_leaving && (
                            <p className="text-xs mt-0.5" style={{ color: "rgba(255,255,255,0.25)" }}>Left: {job.reason_for_leaving}</p>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Notes */}
            {profile?.notes && (
              <div className="px-4 py-3" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)" }}>
                <p className="font-head text-[9px] uppercase tracking-[0.3em] mb-1.5" style={{ color: "rgba(255,255,255,0.3)" }}>Notes</p>
                <p className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>{profile.notes}</p>
              </div>
            )}

            {/* Chat section */}
            <AnimatePresence>
              {showChat && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  style={{ border: `1px solid rgba(255,77,0,0.2)` }}>
                  <div className="px-4 py-2.5 flex items-center gap-2"
                    style={{ background: "rgba(255,77,0,0.06)", borderBottom: "1px solid rgba(255,77,0,0.15)" }}>
                    <MessageSquare className="w-3.5 h-3.5" style={{ color: ACCENT }} />
                    <p className="font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: ACCENT }}>Direct Message — {driverName}</p>
                  </div>
                  <ChatRoom
                    room={chatRoom}
                    roomLabel={`Direct: ${driverName}`}
                    token={token}
                    hub={hub}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function MyDriversPanel({ token, hub }) {
  const [drivers, setDrivers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.marketGetUnlockedDrivers(token);
      setDrivers(r.unlocked || []);
    } catch { toast.error("Failed to load your drivers"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} /></div>
  );

  return (
    <div className="space-y-4">
      <div>
        <p className="font-head text-[9px] uppercase tracking-[0.4em] text-white/30 mb-0.5">Carrier Hub</p>
        <h2 className="font-head text-xl font-black uppercase tracking-[0.04em]" style={{ color: ACCENT }}>My Drivers</h2>
        <p className="text-xs text-white/30 mt-0.5">Drivers whose full profiles and direct messaging you have unlocked.</p>
      </div>

      {drivers.length === 0 ? (
        <div className="flex flex-col items-center py-16 gap-3" style={{ border: "1px dashed rgba(255,255,255,0.07)" }}>
          <Truck className="w-10 h-10 opacity-10" />
          <p className="font-head text-xs uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.2)" }}>No drivers transferred yet</p>
          <p className="text-xs text-center max-w-xs" style={{ color: "rgba(255,255,255,0.15)" }}>
            Once you complete a placement payment and admin approves it, the driver&apos;s full profile and direct messaging will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {drivers.map((d) => (
            <button key={d.bid_id} onClick={() => setSelected(d)}
              data-testid={`my-driver-row-${d.app_id}`}
              className="w-full flex items-center gap-4 px-4 py-4 text-left transition-all hover:brightness-110"
              style={{ background: CARD, border: "1px solid rgba(255,255,255,0.06)" }}>
              {/* Avatar */}
              <div className="w-10 h-10 shrink-0 flex items-center justify-center font-head text-sm font-black"
                style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)", color: GREEN }}>
                {d.driver_name.split(" ").map((n) => n[0] || "").join("").slice(0, 2).toUpperCase()}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-head text-sm font-black text-white truncate">{d.driver_name}</p>
                <p className="text-[10px] truncate" style={{ color: "rgba(255,255,255,0.3)" }}>
                  {[d.position_type?.replace(/_/g, " "), d.pay_program?.replace(/_/g, " ")].filter(Boolean).join(" · ")}
                  {d.transferred_at && <span> · {new Date(d.transferred_at).toLocaleDateString()}</span>}
                </p>
              </div>
              {/* Badges */}
              <div className="flex items-center gap-2 shrink-0">
                <span className="flex items-center gap-1 px-2 py-1 font-head text-[8px] uppercase tracking-[0.1em]"
                  style={{ background: "rgba(34,197,94,0.08)", color: GREEN, border: "1px solid rgba(34,197,94,0.2)" }}>
                  <Unlock className="w-2.5 h-2.5" /> Unlocked
                </span>
                <span className="flex items-center gap-1 px-2 py-1 font-head text-[8px] uppercase tracking-[0.1em]"
                  style={{ background: "rgba(255,77,0,0.08)", color: ACCENT, border: "1px solid rgba(255,77,0,0.2)" }}>
                  <MessageSquare className="w-2.5 h-2.5" /> Chat
                </span>
                <ChevronRight className="w-4 h-4" style={{ color: "rgba(255,255,255,0.2)" }} />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Full profile modal */}
      <AnimatePresence>
        {selected && (
          <FullProfileModal
            appId={selected.app_id}
            driverName={selected.driver_name}
            bidId={selected.bid_id}
            token={token}
            hub={hub}
            onClose={() => setSelected(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
